/**
 * Request-a-change modal for Settings > My-School read-only fields
 * (Plan 13).
 *
 * SCHOOL_ADMIN clicks "Request a change" on a read-only field
 * (school name, federal-program enrollment, CEP designation,
 * school-account coverage, grade ranges) → this Sheet opens prefilled
 * with the field + current value. Submitting creates a Plan-27 task
 * routed to ORDO_ADMIN with the urgency-derived SLA target.
 *
 * Rendered as a right-side Sheet (matches the rest of the apps/ordo
 * surface — `TopUpSheet`, `WalletSharingToggleSheet`, etc.).
 *
 * Schema gap: the BlockingTask `dueAt` is computed server-side from
 * the urgency by `LocationSettingsManager.dueDateForUrgency`. The
 * Plan-12 escalation cron is what actually pings on overdue tasks
 * (separate plan).
 */

import { Button } from '@ordo/ui/button'
import { Label } from '@ordo/ui/label'
import { RadioGroup, RadioGroupItem } from '@ordo/ui/radio-group'
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@ordo/ui/sheet'
import { toast } from '@ordo/ui/sonner'
import { Textarea } from '@ordo/ui/textarea'
import { useEffect, useState } from 'react'

import { type SchoolChangeRequestUrgency, useCreateSchoolChangeRequestMutation } from '@/api/location-settings'

export interface RequestChangeModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  locationId: string
  field: string
  /** Human-friendly field label (e.g. "School name", "CEP designation"). */
  fieldLabel: string
  currentValue: string | null
}

const URGENCY_OPTIONS: Array<{ value: SchoolChangeRequestUrgency; label: string; description: string }> = [
  {
    value: 'ROUTINE',
    label: 'Routine',
    description: '5 business day SLA. Use for most field changes.',
  },
  {
    value: 'TIME_SENSITIVE',
    label: 'Time-sensitive',
    description: '1 business day SLA. Use when this blocks an upcoming menu publish or order cycle.',
  },
  {
    value: 'URGENT',
    label: 'Urgent',
    description: 'Same-day SLA, escalates via SMS once Plan 36 dispatcher lands. Use sparingly.',
  },
]

export function RequestChangeModal({
  open,
  onOpenChange,
  locationId,
  field,
  fieldLabel,
  currentValue,
}: RequestChangeModalProps) {
  const [proposedValue, setProposedValue] = useState('')
  const [reason, setReason] = useState('')
  const [urgency, setUrgency] = useState<SchoolChangeRequestUrgency>('ROUTINE')
  const mutation = useCreateSchoolChangeRequestMutation()

  // Reset the form whenever the modal opens — so re-opening for a
  // different field doesn't leak the prior payload in.
  useEffect(() => {
    if (open) {
      setProposedValue('')
      setReason('')
      setUrgency('ROUTINE')
    }
  }, [open, field])

  const canSubmit = proposedValue.trim().length > 0 && !mutation.isPending

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) {
      return
    }
    try {
      await mutation.mutateAsync({
        locationId,
        field,
        currentValue,
        proposedValue: proposedValue.trim(),
        reason: reason.trim(),
        urgency,
      })
      toast.success('Change request submitted', {
        description: 'An Ordo admin will review and respond within the SLA window.',
      })
      onOpenChange(false)
    } catch (err) {
      toast.error('Could not submit change request', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" size="lg" className="flex flex-col gap-0 p-0">
        <SheetHeader className="shrink-0 border-b px-6 pb-4 pt-6">
          <SheetTitle>Request a change: {fieldLabel}</SheetTitle>
          <SheetDescription>
            Ordo-provisioned fields can't be edited directly — they have compliance and contractual impact. Submit a
            request and an Ordo admin will review.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col gap-0 overflow-hidden">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5 text-sm">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Field</Label>
              <p className="font-medium">{fieldLabel}</p>
            </div>

            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Current value</Label>
              <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm">{currentValue ?? '—'}</p>
            </div>

            <div className="space-y-1">
              <Label htmlFor="proposed-value">Proposed new value</Label>
              <Textarea
                id="proposed-value"
                value={proposedValue}
                onChange={(e) => setProposedValue(e.target.value)}
                placeholder="What should it change to?"
                required
                rows={3}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="reason">Reason / context (optional)</Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this changing? Any compliance or billing context the reviewer should know."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Urgency</Label>
              <RadioGroup
                value={urgency}
                onValueChange={(v) => setUrgency(v as SchoolChangeRequestUrgency)}
                className="flex flex-col gap-2">
                {URGENCY_OPTIONS.map((opt) => (
                  <Label
                    key={opt.value}
                    htmlFor={`urgency-${opt.value}`}
                    className="flex cursor-pointer items-start gap-3 rounded-md border bg-card p-3 hover:bg-accent/50">
                    <RadioGroupItem id={`urgency-${opt.value}`} value={opt.value} className="mt-0.5" />
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-medium">{opt.label}</span>
                      <span className="text-xs text-muted-foreground">{opt.description}</span>
                    </div>
                  </Label>
                ))}
              </RadioGroup>
            </div>
          </div>

          <SheetFooter className="shrink-0 border-t px-6 py-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {mutation.isPending ? 'Submitting…' : 'Submit request'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
